"use client";

import { motion } from "framer-motion";

// ICONS
const WindIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M2 12h20M2 6h10M2 18h15" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SnowIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M12 2v20M4 7l16 10M4 17L20 7" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const WrenchIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4z" strokeWidth="2" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <circle cx="12" cy="12" r="3" strokeWidth="2" />
    <path d="M19.4 15a2 2 0 0 0 .3 2l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a2 2 0 0 0-2-.3 2 2 0 0 0-1 1.7V22a2 2 0 1 1-4 0v-.1a2 2 0 0 0-1-1.7 2 2 0 0 0-2 .3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a2 2 0 0 0 .3-2 2 2 0 0 0-1.7-1H2a2 2 0 1 1 0-4h.1a2 2 0 0 0 1.7-1 2 2 0 0 0-.3-2l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a2 2 0 0 0 2 .3h.2a2 2 0 0 0 1-1.7V2a2 2 0 1 1 4 0v.1a2 2 0 0 0 1 1.7 2 2 0 0 0 2-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a2 2 0 0 0-.3 2v.2a2 2 0 0 0 1.7 1H22a2 2 0 1 1 0 4h-.1a2 2 0 0 0-1.7 1z" strokeWidth="2" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M12 2l7 4v6c0 5-3 9-7 10-4-1-7-5-7-10V6l7-4z" strokeWidth="2" />
  </svg>
);

const PackageIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M21 8l-9-5-9 5 9 5 9-5z" strokeWidth="2" />
    <path d="M3 8v8l9 5 9-5V8" strokeWidth="2" />
  </svg>
);

// DATA
const services = [
  {
    icon: <WindIcon />,
    title: "HVAC Coil Manufacturing",
    desc: "Precision-engineered evaporator & condenser coils built for durability, efficiency, and industrial-grade performance."
  },
  {
    icon: <SnowIcon />,
    title: "Cooling System Solutions",
    desc: "Energy-efficient cooling systems for residential, commercial, and industrial environments."
  },
  {
    icon: <WrenchIcon />,
    title: "Installation Services",
    desc: "Professional installation ensuring system balance, airflow efficiency, and long-term reliability."
  },
  {
    icon: <SettingsIcon />,
    title: "Maintenance & Repair",
    desc: "Preventive maintenance and fast repair services to ensure uninterrupted HVAC performance."
  },
  {
    icon: <ShieldIcon />,
    title: "Quality Assurance",
    desc: "Strict testing standards ensuring safety, durability, and compliance with HVAC benchmarks."
  },
  {
    icon: <PackageIcon />,
    title: "Custom HVAC Fabrication",
    desc: "Tailor-made HVAC coil solutions built according to exact project requirements."
  }
];

// ANIMATION
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0 }
};

export default function ServicesSection() {
  return (
    <section className="py-24 sm:py-28 bg-black text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 sm:mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Our Services
          </h2>

          <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-sm sm:text-base leading-7">
            Delivering industrial-grade HVAC solutions with precision engineering,
            reliability, and long-term performance.
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
        >
          {services.map((item, index) => (
            <motion.div
              key={index}
              variants={item}
              whileHover={{ y: -10, scale: 1.03 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="relative group bg-white text-gray-900 rounded-2xl p-7 sm:p-8 shadow-md hover:shadow-2xl overflow-hidden"
            >

              {/* hover glow */}
              <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-5 transition duration-500" />

              {/* icon */}
              <div className="text-[#1f4ba5] mb-4 relative z-10">
                {item.icon}
              </div>

              {/* title */}
              <h3 className="text-xl font-semibold mb-2 relative z-10">
                {item.title}
              </h3>

              {/* desc */}
              <p className="text-gray-600 text-sm leading-relaxed relative z-10">
                {item.desc}
              </p>

              {/* underline */}
              <div className="h-[2px] w-0 group-hover:w-1/2 bg-[#1f4ba5] transition-all duration-500 mt-5" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}